import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Avatar,
  makeStyles,
  createStyles,
  Card,
  Button,
  Link,
} from '@material-ui/core';
import { nameRepresentation } from '../../utils/text-formatting';
import { CustomDrawer } from '../../components/custom-drawer/custom-drawer';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useStaticQuery, graphql } from 'gatsby';
import { MembershipTrainerData } from '../../templates/trainer-membership';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';

interface Props {
  trainerData: MembershipTrainerData;
}

const useStyles = makeStyles(({ palette, spacing }) =>
  createStyles({
    card: {
      backgroundColor: palette.primary.main,
      color: palette.primary.contrastText,
      marginTop: spacing(2),
      padding: spacing(2, 3),
    },
    avatar: {
      width: 100,
      height: 100,
    },
    buttonsContainer: {
      maxWidth: 250,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  })
);

export const ConnectWithTrainer: React.FC<Props> = ({ trainerData }) => {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const name = nameRepresentation(
    trainerData.first_name,
    trainerData.last_name
  );

  const whatsAppText = encodeURIComponent(
    'Hi there,\nI’ve just started my membership.\nLooking forward to connect!'
  );
  const facebookLink = `${trainerData.m_me_url}?ref=get_started`;
  const whatsAppLink = `${trainerData.whatsapp_url}?text=${whatsAppText}`;
  return (
    <Box>
      <Card className={classes.card}>
        <Typography variant="h6">Congrats, you are all set!</Typography>
        <Box mt={2} />
        <Typography>
          Subscription for remote personal training was confirmed.
        </Typography>
        <Box
          mt={3}
          mb={6}
          display="flex"
          flexDirection="column"
          alignItems="center"
          marginLeft="auto"
          marginRight="auto"
        >
          <Avatar
            src={trainerData.profile_pic_url}
            className={classes.avatar}
            alt={`${name} Profile Picture`}
          />
          <Box mt={2} />
          <Typography variant="subtitle1">{name}</Typography>
        </Box>
        <Button
          onClick={() => setDrawerOpen(true)}
          size="large"
          fullWidth
          variant="contained"
          color="default"
        >
          CONNECT WITH TRAINER
        </Button>
        <CustomDrawer
          title="Connect with your trainer"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        >
          <Box
            mt={2}
            mb={3}
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Avatar
              src={trainerData.profile_pic_url}
              className={classes.avatar}
              alt={`${name} Profile Picture`}
            />
            <Box mt={2} />
            <Typography variant="subtitle1">Message {name} via</Typography>
          </Box>
          <Box className={classes.buttonsContainer}>
            <Button
              href={facebookLink}
              target="_blank"
              size="large"
              color="primary"
              variant="contained"
              fullWidth
              startIcon={<svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M20 4.76562C11.198 4.76562 4.375 11.2131 4.375 19.9219C4.375 24.4771 6.24188 28.4134 9.28213 31.1322C9.53734 31.3606 9.69141 31.6806 9.70187 32.0231L9.78697 34.8025C9.81416 35.6891 10.7299 36.2659 11.5412 35.9078L14.6425 34.5388C14.9054 34.4228 15.2 34.4013 15.477 34.4775C16.9022 34.8694 18.419 35.0781 20 35.0781C28.802 35.0781 35.625 28.6306 35.625 19.9219C35.625 11.2131 28.802 4.76562 20 4.76562Z" fill="white"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M10.6172 24.3543L15.2071 17.0724C15.9371 15.914 17.5006 15.6256 18.5961 16.4471L22.2466 19.185C22.5815 19.4363 23.0424 19.4349 23.3759 19.1818L28.3061 15.4401C28.9642 14.9407 29.8231 15.7282 29.3828 16.4271L24.7929 23.709C24.0628 24.8674 22.4993 25.1559 21.4039 24.3342L17.7533 21.5963C17.4184 21.3451 16.9575 21.3464 16.624 21.5996L11.6938 25.3413C11.0357 25.8406 10.1767 25.0531 10.6172 24.3543Z" fill="#6C63FF"/>
              </svg>
              }
            >
              Facebook Messenger
              <Box mr={1} />
            </Button>
            <Box mt={2} />
            <Button
              href={whatsAppLink}
              target="_blank"
              size="large"
              color="primary"
              variant="contained"
              startIcon={<WhatsAppIcon style={{ fontSize: 30 }} />}
              fullWidth
            >
              WhatsApp Messenger
            </Button>
          </Box>
          <Box my={6}>
            <Alert severity="warning">
              <AlertTitle>Warning</AlertTitle>
              Based on our policies the trainer is allowed to respond to you
              within 24 hours. If you have any questions please contact the{' '}
              <Link
                color="inherit"
                underline="always"
                href={`mailto:team@habfit.io?subject=Membership%20with%20Trainer%20${trainerData.first_name}%20${trainerData.last_name}`}
              >
                HabFit Support Team
              </Link>
              .
            </Alert>
          </Box>
        </CustomDrawer>
      </Card>
    </Box>
  );
};
